import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useQuote } from "@/context/QuoteContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty, clear, totalCount } = useQuote();
  const [step, setStep] = useState("cart"); // cart | form | done
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: "", hotel_name: "", email: "", phone: "", message: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-requests`, { ...form, items, page_url: window.location.href });
      toast.success("Thank you. Your inquiry has been submitted successfully. Our team will contact you within 24 business hours.");
      setStep("done");
      clear();
      setForm({ full_name: "", hotel_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        data-testid="quote-drawer-backdrop"
        className="fixed inset-0 bg-black/40 z-50"
        onClick={() => setIsOpen(false)}
      />
      <aside
        data-testid="quote-drawer"
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white border-l border-[#D4AF37]/30 shadow-2xl z-50 flex flex-col"
      >
        <header className="p-6 border-b border-[#E5E5E5] flex justify-between items-center bg-[#FAF9F6]">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-serif-display text-2xl">Your Quote</h2>
            <span className="text-xs text-[#4A4A4A] uppercase tracking-wider">({totalCount} items)</span>
          </div>
          <button data-testid="close-quote-drawer-btn" onClick={() => setIsOpen(false)} className="p-1 hover:text-[#D4AF37]">
            <X className="w-5 h-5" />
          </button>
        </header>

        {step === "cart" && (
          <>
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-10 text-center text-[#4A4A4A]">
                  <p className="font-serif-display text-xl mb-3">Your quote list is empty</p>
                  <p className="text-sm">Browse our categories and add items to request your factory-direct price.</p>
                </div>
              ) : (
                items.map((item) => (
                  <div key={item.product_id} data-testid={`quote-item-${item.product_id}`} className="flex items-center gap-4 p-4 border-b border-[#E5E5E5]">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover bg-[#FAF9F6]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-[#4A4A4A] uppercase tracking-wider mt-1">{item.category}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button data-testid={`decrease-qty-${item.product_id}`} onClick={() => updateQty(item.product_id, item.quantity - 1)} className="p-1 border border-[#E5E5E5] hover:border-[#D4AF37]">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span data-testid={`qty-${item.product_id}`} className="text-sm w-8 text-center">{item.quantity}</span>
                        <button data-testid={`increase-qty-${item.product_id}`} onClick={() => updateQty(item.product_id, item.quantity + 1)} className="p-1 border border-[#E5E5E5] hover:border-[#D4AF37]">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <button data-testid={`remove-${item.product_id}`} onClick={() => removeItem(item.product_id)} className="p-2 text-[#4A4A4A] hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
            {items.length > 0 && (
              <footer className="p-6 border-t border-[#E5E5E5] bg-[#FAF9F6]">
                <button
                  data-testid="proceed-to-quote-form-btn"
                  onClick={() => setStep("form")}
                  className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-white py-4 uppercase tracking-[0.2em] text-sm transition-colors"
                >
                  Request Factory-Direct Quote
                </button>
              </footer>
            )}
          </>
        )}

        {step === "form" && (
          <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-4">
            <p className="text-sm text-[#4A4A4A]">Tell us where to send your custom quote. We respond within 24 hours.</p>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Full Name *</label>
              <input data-testid="quote-form-name" required name="full_name" value={form.full_name} onChange={handleChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-2 bg-transparent" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Hotel / Property</label>
              <input data-testid="quote-form-hotel" name="hotel_name" value={form.hotel_name} onChange={handleChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-2 bg-transparent" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Email *</label>
              <input data-testid="quote-form-email" type="email" required name="email" value={form.email} onChange={handleChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-2 bg-transparent" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Phone</label>
              <input data-testid="quote-form-phone" name="phone" value={form.phone} onChange={handleChange} className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-2 bg-transparent" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Notes</label>
              <textarea data-testid="quote-form-message" name="message" rows={3} value={form.message} onChange={handleChange} className="mt-2 w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none py-2 px-3 bg-transparent" />
            </div>
            <div className="flex gap-3 pt-4">
              <button type="button" data-testid="quote-form-back" onClick={() => setStep("cart")} className="flex-1 border border-[#1A1A1A] py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-white transition-colors">Back</button>
              <button type="submit" data-testid="quote-form-submit" disabled={submitting} className="flex-1 bg-[#D4AF37] hover:bg-[#B5952F] text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60">
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        )}

        {step === "done" && (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-5">
              <ShoppingBag className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h3 className="font-serif-display text-2xl mb-3">Thank you!</h3>
            <p className="text-sm text-[#4A4A4A] mb-6">Your quote request has been received. Our team will contact you within 24 hours with factory-direct pricing.</p>
            <button data-testid="close-quote-success-btn" onClick={() => { setStep("cart"); setIsOpen(false); }} className="bg-[#1A1A1A] hover:bg-[#D4AF37] text-white px-8 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
              Close
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default QuoteDrawer;
